import { createStore } from '@udecode/zustood';
import { type ValidationError } from 'formj';
import { nanoid } from 'nanoid';
import {
    type CommitFile,
    type PullRequestFile,
    type PullRequestFileWithBaseContent,
} from '../api/code-forge/interface';
import { type CompanyRecord } from '../dade-data/schema';
import { toss } from '../util/error';

export type File = {
    /** Unique identifier for the file in the store. This is a random ID. */
    id: Readonly<string>;

    /**
     * Has this record already been touched by the pull request or was it newly added/modified in this data-editor
     * session?
     */
    touchedByPullRequest: Readonly<boolean>;
    /** The status of the file compared to the base branch. */
    statusComparedToBase: 'added' | 'modified' | 'unchanged';

    /**
     * The state of the file on the PR head branch.
     *
     * This will be `undefined` if the file is newly added in this data-editor session. For newly updated files in this
     * session, we are simulating `head = base` even in new PRs where the head branch is not yet created.
     */
    head:
        | Readonly<{
              /** The filename as currently committed on the PR head branch. */
              filename: string;
              /** The record as currently committed on the PR head branch. */
              record: Partial<CompanyRecord>;
          }>
        | undefined;

    /**
     * The state of the file on the PR base branch.
     *
     * This will be `undefined` if the record is newly added in the PR or this data-editor session and doesn't exist yet
     * on the base branch.
     */
    base:
        | Readonly<{
              /** The file content as on the base branch. */
              content: string;
          }>
        | undefined;

    /** The (uncommitted) changes to the file made by the user in this data-editor session. */
    editorChanges: {
        /**
         * The changed filename as of this data-editor session.
         *
         * Will be `undefined` if the filename has not been changed.
         */
        filename: string | undefined;
        /**
         * The changed record as of this data-editor session.
         *
         * Will be `undefined` if the record has not been changed.
         */
        record: Partial<CompanyRecord> | undefined;
    };

    validationErrors: ValidationError[];
};

const fileIsChangedComparedToBase = (f: File) =>
    (f.editorChanges.filename !== undefined && f.head?.filename !== f.editorChanges.filename) ||
    (f.editorChanges.record !== undefined && JSON.stringify(f.editorChanges.record) !== JSON.stringify(f.head?.record));

/** The PR store represents both PRs newly created by the user as well as PRs for review. */
export const prStore = createStore('pr')(
    {
        files: [] as File[],
    },
    { devtools: { enabled: process.env['NODE_ENV'] === 'development' } }
)
    .extendSelectors((state) => ({
        getFile: (id: string): File | undefined => state.files.find((f) => f.id === id),

        getChangedFiles: () =>
            state.files.filter(
                (f) =>
                    // If an added file wasn't already touched by the PR, it is definitely changed.
                    (!f.touchedByPullRequest && f.statusComparedToBase === 'added') ||
                    // Otherwise, the filename and/or record needs to have changed compared to the PR head branch.
                    fileIsChangedComparedToBase(f)
            ),
    }))
    .extendSelectors((state, get) => ({
        /** Suggest a title for this PR. */
        getSuggestedTitle: () => {
            const filesChangedInEditor = get.getChangedFiles();
            const filesChangedInOriginalPr = state.files.filter((f) => f.touchedByPullRequest);
            const files = [...new Set([...filesChangedInEditor, ...filesChangedInOriginalPr])];

            if (files.length === 0) return '';

            const newName = (f: File) =>
                f.editorChanges.record?.name ||
                f.head?.record.name ||
                f.editorChanges.filename ||
                f.head?.filename ||
                '<unknown>';

            const added = files.filter((f) => f.statusComparedToBase === 'added').map((f) => `'${newName(f)}'`);
            const updated = files.filter((f) => f.statusComparedToBase === 'modified').map((f) => `'${newName(f)}'`);

            const parts = [];
            if (added.length > 0) parts.push(`Add ${added.join(', ')}`);
            if (updated.length > 0) parts.push(`Update ${updated.join(', ')}`);

            return parts.join('; ') || 'Modify company records';
        },

        /** Get the files in the format required by the code forge interface for committing. */
        toCommitFiles: (): CommitFile[] =>
            get.getChangedFiles().map((f) => {
                const filename = f.head?.filename || f.editorChanges.filename;
                if (!filename)
                    throw new Error('Cannot create commit file without a filename. This should never happen.');

                return {
                    filename,
                    renamedFilename: f.editorChanges.filename,
                    content: JSON.stringify(f.editorChanges.record || f.head?.record, null, 4) + '\n',
                };
            }),
    }))
    .extendSelectors((state, get) => ({
        hasChanges: () => get.getChangedFiles().length > 0,
        hasErrors: () => state.files.some((f) => f.validationErrors.length > 0),
    }))
    .extendActions((set) => ({
        reset: () => set.files([]),

        initWithFilesFromPullRequest: (files: (PullRequestFile | PullRequestFileWithBaseContent)[]) =>
            set.files(
                files.map((f) => ({
                    id: nanoid(),

                    touchedByPullRequest: true,
                    statusComparedToBase:
                        f.status === 'added' || f.status === 'modified'
                            ? f.status
                            : toss(new Error(`Unsupported file status in "${f.filename}" ("${f.status}")`)),

                    head: {
                        filename: f.filename,
                        record: JSON.parse(f.content),
                    },

                    base:
                        'baseContent' in f
                            ? {
                                  content: f.baseContent,
                              }
                            : undefined,

                    editorChanges: {
                        filename: undefined,
                        record: undefined,
                    },

                    validationErrors: [],
                }))
            ),

        /**
         * Add a completely new record to the PR that didn't already exist on the main branch and hasn't already been
         * added in the PR either.
         */
        addNewFile: () => {
            const id = nanoid();

            set.state((draft) => {
                draft.files.push({
                    id,

                    touchedByPullRequest: false,
                    statusComparedToBase: 'added',

                    head: undefined,

                    base: undefined,

                    editorChanges: {
                        filename: `companies/${id}.json`,
                        record: {},
                    },

                    validationErrors: [],
                });
            });
        },
        /** Add an existing record to the PR to update it. */
        addFileForUpdate: (filename: string, content: string) => {
            set.state((draft) => {
                // Don't add if it's already there.
                if (draft.files.some((f) => f.editorChanges.filename === filename || f.head?.filename === filename))
                    return;

                draft.files.push({
                    id: nanoid(),

                    touchedByPullRequest: false,
                    statusComparedToBase: 'unchanged',

                    head: {
                        filename,
                        record: JSON.parse(content),
                    },

                    base: {
                        content,
                    },

                    editorChanges: {
                        filename,
                        record: JSON.parse(content),
                    },

                    validationErrors: [],
                });
            });
        },

        /**
         * Remove a file from the store, effectively discarding the changes made to it in this session.
         *
         * This does **not** remove the file from the pull request.
         */
        discardFileChanges: (id: string) => set.state((draft) => ({ files: draft.files.filter((f) => f.id !== id) })),

        setForFile: (id: string, changes: Partial<File>) => {
            set.state((draft) => {
                const fileToUpdate = draft.files.find((f) => f.id === id);
                if (!fileToUpdate) throw new Error('File not found for selector');

                Object.assign(fileToUpdate, changes);

                if (['modified', 'unchanged'].includes(fileToUpdate.statusComparedToBase))
                    fileToUpdate.statusComparedToBase = fileIsChangedComparedToBase(fileToUpdate)
                        ? 'modified'
                        : 'unchanged';
            });
        },
    }));
